import React from 'react';
import { getFieldDependentTable } from './tables.js';
import { persistedClientSideTable, useClientSideRevision } from '../persistentMetadataCache.js';
import type { ProcessedTableMetadata, GlobalSettings } from '../../types/metadata.js';
import type { BaseRecord } from '../../types/api.js';

// Interface for data loading results
interface DataLoadResult {
  tableName: string;
  data: BaseRecord[];
}

// Hook result interface
interface UseClientSideDataResult {
  data: Record<string, BaseRecord[]>;
  isLoading: boolean;
  error: Error | null;
}

interface LoadedClientSideData {
  tableData: ProcessedTableMetadata;
  globalSettingsData: GlobalSettings;
  isAdmin: boolean;
  data: Record<string, BaseRecord[]>;
  error: Error | null;
}

const EMPTY_CLIENT_SIDE_DATA: Record<string, BaseRecord[]> = {};

// Workspace Gmail configuration is restricted to super admins. These tables
// must not be fetched as part of normal users' generic client-side metadata.
const ADMIN_ONLY_CLIENT_SIDE_TABLES = new Set([
  'gmail_provider',
  'gmail_mailbox',
  'gmail_sync_interest',
]);

/**
 * Hook to load the rows of every client_side table
 * @param tableData - Processed table metadata
 * @param globalSettingsData - Global settings (loading waits for them)
 * @param isAdmin - Whether the signed-in user can read admin-only configuration
 * @returns Client-side data with loading and error states
 */
export const useClientSideData = (
  tableData: ProcessedTableMetadata | null, 
  globalSettingsData: GlobalSettings | null,
  isAdmin: boolean
): UseClientSideDataResult => {
  const [loaded, setLoaded] = React.useState<LoadedClientSideData | null>(null);
  // Bumped when persisted rows were found stale: read the tables again.
  const revision = useClientSideRevision();

  React.useEffect(() => {
    if (!tableData?.asList || !globalSettingsData) return;
    let cancelled = false;

    const loadClientSideTables = async () => {
      const finalClientSideTables = tableData.asList
        .filter((table) => table.client_side === true)
        .filter((table) => (
          isAdmin || table.schema_name !== 'core_crm' || !ADMIN_ONLY_CLIENT_SIDE_TABLES.has(table.table_name)
        ))
        .map((table) => table.table_name);

      // If no client-side tables, we're done
      if (finalClientSideTables.length === 0) {
        if (!cancelled) {
          setLoaded({ tableData, globalSettingsData, isAdmin, data: EMPTY_CLIENT_SIDE_DATA, error: null });
        }
        return;
      }

      // Parallel data loading for all client-side tables
      const dataLoadPromises = finalClientSideTables.map(async (tableName): Promise<DataLoadResult> => {
        try {
          const table = tableData.asList.find(t => t.table_name === tableName);
          if (!table) {
            throw new Error(`Table ${tableName} not found in metadata`);
          }
          
          const { data, error } = await getFieldDependentTable({
            tableName,
            schema: table.schema_name,
            conditions: undefined,
            orderBy: undefined,
            limitOffset: undefined,
            withCount: false,
            // Consumers only read labels and plain attributes; pages that show
            // calculated columns (funder list, lead sources) query them directly.
            excludeCalculated: true,
            tableMetadata: tableData,
            meta: persistedClientSideTable(table.schema_name, tableName),
          });
          
          if (error) {
            throw error;
          }
          
          return { tableName, data: Array.isArray(data) ? data : data ? [data] : [] };
        } catch (error) {
          console.error(`Failed to load client-side data for table ${tableName}:`, error);
          throw error;
        }
      });

      try {
        const dataResults = await Promise.all(dataLoadPromises);
        const newClientSideData: Record<string, BaseRecord[]> = {};
        
        dataResults.forEach(({ tableName, data }) => {
          newClientSideData[tableName] = data;
        });

        if (!cancelled) {
          setLoaded({ tableData, globalSettingsData, isAdmin, data: newClientSideData, error: null });
        }
      } catch (loadError) {
        if (!cancelled) {
          setLoaded({
            tableData,
            globalSettingsData,
            isAdmin,
            data: EMPTY_CLIENT_SIDE_DATA,
            error: loadError instanceof Error ? loadError : new Error(String(loadError)),
          });
        }
      }
    };

    void loadClientSideTables();
    return () => { cancelled = true; };
  }, [tableData, globalSettingsData, isAdmin, revision]);

  if (loaded?.tableData !== tableData || loaded.globalSettingsData !== globalSettingsData || loaded.isAdmin !== isAdmin) {
    return { data: EMPTY_CLIENT_SIDE_DATA, isLoading: true, error: null };
  }

  return { data: loaded.data, isLoading: false, error: loaded.error };
};
